import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { motion } from "framer-motion";
import { FaCoins, FaEdit, FaHistory } from "react-icons/fa";

// Component chính
const AdminUserCoinBalance = () => {
  const { fetchWithRetry } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newCoinBalance, setNewCoinBalance] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [coinFilter, setCoinFilter] = useState("");
  const [editHistory, setEditHistory] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Lấy danh sách người dùng và số dư coin
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await fetchWithRetry({
          method: "get",
          url: "/api/admin/users",
        });
        setUsers(response.data || []);
        setFilteredUsers(response.data || []);
      } catch (err) {
        setError(err.message || "Không thể tải danh sách người dùng!");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [fetchWithRetry]);

  // Tìm kiếm và lọc người dùng
  useEffect(() => {
    let filtered = users;

    // Tìm kiếm theo tên hoặc email
    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Lọc theo số dư coin
    if (coinFilter) {
      const coinValue = Number(coinFilter);
      if (!isNaN(coinValue)) {
        filtered = filtered.filter((user) => (user.coinBalance || 0) >= coinValue);
      }
    }

    setFilteredUsers(filtered);
  }, [searchTerm, coinFilter, users]);

  // Cập nhật số dư coin của người dùng
  const handleUpdateCoinBalance = async () => {
    try {
      const response = await fetchWithRetry({
        method: "put",
        url: `/api/admin/users/${selectedUser._id}/coin-balance`,
        data: { coinBalance: newCoinBalance },
      });
      setUsers(
        users.map((user) =>
          user._id === selectedUser._id ? { ...user, coinBalance: response.data.coinBalance } : user
        )
      );
      setShowEditModal(false);
      setShowConfirmModal(false);
      setSelectedUser(null);
      setNewCoinBalance(0);
    } catch (err) {
      setError(err.message || "Không thể cập nhật số dư coin!");
    }
  };

  // Lấy lịch sử chỉnh sửa số dư coin
  const handleShowEditHistory = async (userId) => {
    try {
      const response = await fetchWithRetry({
        method: "get",
        url: `/api/admin/users/${userId}/coin-balance-history`,
      });
      setEditHistory(response.data || []);
      setShowHistoryModal(true);
    } catch (err) {
      setError(err.message || "Không thể tải lịch sử chỉnh sửa!");
    }
  };

  if (loading) {
    return <p className="text-center">Đang tải...</p>;
  }

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <h2 className="text-3xl font-extrabold text-gray-900 mb-6 text-center flex items-center justify-center">
        <FaCoins className="mr-2 text-yellow-500" /> Quản lý số dư Coin của người dùng
      </h2>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-4 px-4 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
          >
            Đóng
          </button>
        </div>
      )}

      {/* Tìm kiếm và lọc */}
      <div className="mb-6 flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
        <div className="flex-1">
          <label className="block text-gray-700 font-medium mb-1">Tìm kiếm theo tên hoặc email</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Nhập tên hoặc email..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex-1">
          <label className="block text-gray-700 font-medium mb-1">Lọc theo số dư Coin (lớn hơn hoặc bằng)</label>
          <input
            type="number"
            value={coinFilter}
            onChange={(e) => setCoinFilter(e.target.value)}
            placeholder="Nhập số dư coin..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="0"
          />
        </div>
      </div>

      {/* Danh sách người dùng và số dư coin */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="overflow-x-auto"
      >
        <table className="w-full bg-white rounded-lg shadow-lg">
          <thead>
            <tr className="bg-blue-500 text-white">
              <th className="p-4 text-left">Tên người dùng</th>
              <th className="p-4 text-left">Email</th>
              <th className="p-4 text-left">Số dư Coin</th>
              <th className="p-4 text-left">Hành động</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user._id} className="border-b hover:bg-gray-50 transition duration-200">
                <td className="p-4 text-gray-700">{user.username}</td>
                <td className="p-4 text-gray-700">{user.email}</td>
                <td className="p-4 text-gray-700 flex items-center">
                  <FaCoins className="mr-1 text-yellow-500" /> {user.coinBalance || 0}
                </td>
                <td className="p-4 text-gray-700 flex space-x-2">
                  <button
                    onClick={() => {
                      setSelectedUser(user);
                      setNewCoinBalance(user.coinBalance || 0);
                      setShowEditModal(true);
                    }}
                    className="p-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition duration-200"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedUser(user);
                      handleShowEditHistory(user._id);
                    }}
                    className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
                  >
                    <FaHistory />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </motion.div>

      {/* Modal chỉnh sửa số dư coin */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-md">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Chỉnh sửa số dư Coin của {selectedUser.username}
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setShowConfirmModal(true);
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-gray-700 font-medium mb-1">Số dư Coin</label>
                <input
                  type="number"
                  value={newCoinBalance}
                  onChange={(e) => setNewCoinBalance(Number(e.target.value))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  min="0"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
                >
                  Lưu
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal xác nhận trước khi chỉnh sửa */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-md">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Xác nhận chỉnh sửa</h3>
            <p className="text-gray-700 mb-4">
              Bạn có chắc chắn muốn thay đổi số dư coin của {selectedUser?.username} thành{" "}
              {newCoinBalance} coin?
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
              >
                Hủy
              </button>
              <button
                onClick={handleUpdateCoinBalance}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
              >
                Xác nhận
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal lịch sử chỉnh sửa */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Lịch sử chỉnh sửa số dư Coin của {selectedUser?.username}
            </h3>
            {editHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded-lg shadow-lg">
                  <thead>
                    <tr className="bg-blue-500 text-white">
                      <th className="p-4 text-left">Admin</th>
                      <th className="p-4 text-left">Số Coin thay đổi</th>
                      <th className="p-4 text-left">Ngày chỉnh sửa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editHistory.map((history) => (
                      <tr key={history._id} className="border-b hover:bg-gray-50 transition duration-200">
                        <td className="p-4 text-gray-700">{history.adminId?.username || "N/A"}</td>
                        <td className={`p-4 ${history.coinChange >= 0 ? "text-green-600" : "text-red-600"}`}>
                          {history.coinChange > 0 ? "+" : ""}{history.coinChange} Coin
                        </td>
                        <td className="p-4 text-gray-700">
                          {new Date(history.createdAt).toLocaleString("vi-VN")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-600 text-center">Không có lịch sử chỉnh sửa.</p>
            )}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserCoinBalance;